import { Button } from '@repo/ui/components/button';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

import { LogoText } from '@/components/logo-text';
import { PartnerSwitchDialog } from '@/components/shared';
import { useAcceptInvite } from '@/hooks/mutations/use-accept-invite';
import { useLoginConfirm } from '@/hooks/mutations/use-login-confirm';
import { useInviteStore } from '@/stores';

export const Route = createFileRoute('/(auth)/login/confirm')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      token: String(search.token ?? ''),
      code: String(search.code ?? ''),
    };
  },
  component: ConfirmPage,
});

type AuthStatus =
  | 'authenticating'
  | 'accepting-invite'
  | 'success'
  | 'error'
  | 'invalid';

function ConfirmPage() {
  const { token, code } = Route.useSearch();
  const navigate = useNavigate();
  const loginConfirmMutation = useLoginConfirm();
  const acceptInviteMutation = useAcceptInvite();
  const hasAttempted = useRef(false);

  const { inviteToken, clearInvite } = useInviteStore();

  const [status, setStatus] = useState<AuthStatus>('authenticating');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmationData, setConfirmationData] = useState<{
    currentPartnerName: string;
    newPartnerName: string;
  } | null>(null);

  // Redirect after success with cleanup
  useEffect(() => {
    if (status !== 'success') return;

    const timeoutId = setTimeout(() => {
      navigate({ to: '/home' });
    }, 1500);

    return () => clearTimeout(timeoutId);
  }, [status, navigate]);

  // Authenticate automatically when token and code are present
  useEffect(() => {
    if (!token || !code) {
      setStatus('invalid');
      return;
    }

    if (hasAttempted.current) {
      return;
    }
    hasAttempted.current = true;

    const authenticate = async () => {
      try {
        await loginConfirmMutation.mutateAsync({
          token,
          code,
        });

        // Check if there's a pending invite to accept
        if (inviteToken) {
          setStatus('accepting-invite');
          try {
            const inviteResponse = await acceptInviteMutation.mutateAsync({
              invite_token: inviteToken,
            });

            if (inviteResponse.status === 'requires_confirmation') {
              setConfirmationData({
                currentPartnerName: inviteResponse.current_partner_name,
                newPartnerName: inviteResponse.new_partner_name,
              });
              setShowConfirmDialog(true);
              return;
            }

            // Invite accepted successfully
            clearInvite();
          } catch {
            // Failed to accept invite, but user is logged in - continue to home
            clearInvite();
          }
        }

        setStatus('success');
      } catch {
        setStatus('error');
      }
    };

    authenticate();
  }, [
    token,
    code,
    loginConfirmMutation,
    inviteToken,
    acceptInviteMutation,
    clearInvite,
  ]);

  const handleConfirmSwitch = async () => {
    if (!inviteToken) return;

    try {
      await acceptInviteMutation.mutateAsync({
        invite_token: inviteToken,
        confirm: true,
      });
      clearInvite();
      setShowConfirmDialog(false);
      navigate({ to: '/home' });
    } catch {
      // Error handled by mutation
    }
  };

  const handleCancelSwitch = () => {
    clearInvite();
    setShowConfirmDialog(false);
    navigate({ to: '/home' });
  };

  return (
    <div className="flex flex-col items-center justify-center px-4 py-16">
      <div className="w-full max-w-sm space-y-8">
        <div className="text-center">
          <Link to="/" className="inline-block">
            <LogoText className="text-4xl" />
          </Link>
        </div>

        {(status === 'authenticating' || status === 'accepting-invite') && (
          <div className="space-y-6 text-center">
            <div className="bg-primary/10 mx-auto flex h-16 w-16 items-center justify-center rounded-full">
              <Loader2 className="text-primary h-8 w-8 animate-spin" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {status === 'accepting-invite'
                  ? 'Aceitando convite...'
                  : 'Autenticando...'}
              </h1>
              <p className="text-muted-foreground text-sm">
                {status === 'accepting-invite'
                  ? 'Aguarde enquanto processamos o convite'
                  : 'Aguarde enquanto verificamos seu acesso'}
              </p>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
              <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Autenticado com sucesso!
              </h1>
              <p className="text-muted-foreground text-sm">Redirecionando...</p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/20">
              <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Link inválido ou expirado
              </h1>
              <p className="text-muted-foreground text-sm">
                O link de acesso pode ter expirado ou já foi utilizado. Solicite
                um novo link.
              </p>
            </div>
            <Button asChild className="w-full">
              <Link to="/login">Solicitar novo link</Link>
            </Button>
          </div>
        )}

        {status === 'invalid' && (
          <div className="space-y-6 text-center">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/20">
              <AlertCircle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                Link incompleto
              </h1>
              <p className="text-muted-foreground text-sm">
                O link de acesso está incompleto. Verifique se você copiou o
                link completo do email.
              </p>
            </div>
            <Button asChild className="w-full">
              <Link to="/login">Ir para login</Link>
            </Button>
          </div>
        )}

        {confirmationData && (
          <PartnerSwitchDialog
            open={showConfirmDialog}
            onOpenChange={setShowConfirmDialog}
            currentPartnerName={confirmationData.currentPartnerName}
            newPartnerName={confirmationData.newPartnerName}
            onConfirm={handleConfirmSwitch}
            onCancel={handleCancelSwitch}
            isLoading={acceptInviteMutation.isPending}
          />
        )}
      </div>
    </div>
  );
}
